import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { db } from "../../../../config/firebase";
import { doc, collection, getDocs } from "firebase/firestore";

// ─── Types ─────────────────────────────────────────────────────────────────

interface Props {
  className: string;
  userID: string;
  projectID: string;
  projectTitle: string;
  projectProgress: number;
  categoryScores?: Record<string, number>;
}

interface NextStepRecommendation {
  stepNumber: number;
  title: string;
  reason: string;
  priority: "critical" | "important" | "suggested";
  icon: string;
  category: string;
  categoryScore?: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const CATEGORY_STEPS: Record<string, number[]> = {
  marketOpportunity:    [1, 2, 3, 4, 5],
  customerUnderstanding:[6, 7, 8, 9, 10],
  valueAndProduct:      [11, 12, 13, 14],
  businessModel:        [15, 16, 17, 18, 19],
  validationExecution:  [20, 21, 22, 23, 24],
};

// DESIGN.md category colours
const CATEGORY_COLORS: Record<string, { solid: string; light: string }> = {
  marketOpportunity:    { solid: "#3B82F6", light: "#DBEAFE" },
  customerUnderstanding:{ solid: "#8B5CF6", light: "#EDE9FE" },
  valueAndProduct:      { solid: "#EC4899", light: "#FCE7F3" },
  businessModel:        { solid: "#F59E0B", light: "#FEF3C7" },
  validationExecution:  { solid: "#10B981", light: "#D1FAE5" },
};

const CATEGORY_LABELS: Record<string, string> = {
  marketOpportunity:    "Market Opportunity",
  customerUnderstanding:"Customer Understanding",
  valueAndProduct:      "Value & Product",
  businessModel:        "Business Model",
  validationExecution:  "Validation & Execution",
};

const stepTitles: Record<number, string> = {
  1:  "Market Segmentation",
  2:  "Select a Beachhead Market",
  3:  "Build an End User Profile",
  4:  "Calculate TAM for Beachhead",
  5:  "Profile the Persona",
  6:  "Full Life Cycle Use Case",
  7:  "High-Level Product Specification",
  8:  "Quantify the Value Proposition",
  9:  "Identify Next 10 Customers",
  10: "Define Your Core",
  11: "Chart Competitive Position",
  12: "Determine Customer's DMU",
  13: "Map Process to Acquire Customer",
  14: "Calculate TAM for Follow-on",
  15: "Design a Business Model",
  16: "Set Your Pricing Framework",
  17: "Calculate LTV",
  18: "Map the Sales Process",
  19: "Calculate COCA",
  20: "Identify Key Assumptions",
  21: "Test Key Assumptions",
  22: "Define the MVP",
  23: "Show Dogs Will Eat the Food",
  24: "Develop a Product Plan",
};

// ─── Helper ─────────────────────────────────────────────────────────────────

function getCategoryForStep(stepNum: number): string {
  return (
    Object.entries(CATEGORY_STEPS).find(([, steps]) =>
      steps.includes(stepNum)
    )?.[0] || ""
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export const RecommendedNextStep: React.FC<Props> = ({
  className,
  userID,
  projectID,
  projectTitle,
  projectProgress,
  categoryScores,
}) => {
  const [recommendations, setRecommendations] = useState<NextStepRecommendation[]>([]);
  const [loading, setLoading] = useState(true);

  const analyzeProgress = useCallback(async () => {
    if (!userID || !projectID) return;

    try {
      const userRef  = doc(db, "users", userID);
      const projectRef = doc(userRef, "projects", projectID);
      const progressRef = collection(projectRef, "progress");
      const snapshot = await getDocs(progressRef);

      const stepsData: Record<number, number> = {};
      snapshot.docs.forEach((d) => {
        const n = parseInt(d.id.replace("step", ""), 10);
        stepsData[n] = d.data().stepProgress || 0;
      });

      const recs: NextStepRecommendation[] = [];

      // ── 1. Category-weakness recommendations (when AI scores available) ──
      if (categoryScores) {
        // Sort categories weakest-first
        const catOrder = Object.entries(CATEGORY_STEPS)
          .map(([catId, steps]) => ({
            catId,
            score: categoryScores[catId] ?? 100,
            steps,
          }))
          .sort((a, b) => a.score - b.score);

        for (const { catId, score, steps } of catOrder) {
          if (recs.length >= 3) break;          // cap at 3 category-driven recs
          if (score >= 80) continue;            // strong category — skip

          // First incomplete step in this category
          const incompleteStep = steps.find((s) => (stepsData[s] || 0) < 80);
          if (!incompleteStep) continue;        // all steps done for this category

          const label   = CATEGORY_LABELS[catId] || catId;
          const scoreStr = `${Math.round(score)}%`;
          const reason =
            score < 40
              ? `${label} is your weakest area (${scoreStr}) — this step will boost your overall score the most.`
              : score < 60
              ? `${label} needs attention (${scoreStr}) — completing this step improves it.`
              : `${label} is below target (${scoreStr}) — a quick win to push your score higher.`;

          recs.push({
            stepNumber:    incompleteStep,
            title:         stepTitles[incompleteStep] || `Step ${incompleteStep}`,
            reason,
            priority:      score < 40 ? "critical" : score < 60 ? "important" : "suggested",
            icon:          score < 40 ? "bi-exclamation-triangle" : "bi-arrow-up-circle",
            category:      catId,
            categoryScore: score,
          });
        }
      }

      // ── 2. Next sequential incomplete step (fallback / fill-in) ─────────
      for (let i = 1; i <= 24; i++) {
        if ((stepsData[i] || 0) < 50 && !recs.find((r) => r.stepNumber === i)) {
          recs.push({
            stepNumber: i,
            title:      stepTitles[i] || `Step ${i}`,
            reason:     "Continue where you left off in the validation framework.",
            priority:   "important",
            icon:       "bi-arrow-right-circle",
            category:   getCategoryForStep(i),
          });
          break; // only one sequential suggestion
        }
      }

      // ── 3. Partially complete steps ──────────────────────────────────────
      for (let i = 1; i <= 24 && recs.length < 5; i++) {
        const progress = stepsData[i] || 0;
        if (progress > 0 && progress < 100 && !recs.find((r) => r.stepNumber === i)) {
          recs.push({
            stepNumber: i,
            title:      stepTitles[i] || `Step ${i}`,
            reason:     "Partially complete — review and fill in remaining fields.",
            priority:   "suggested",
            icon:       "bi-pencil",
            category:   getCategoryForStep(i),
          });
        }
      }

      setRecommendations(recs.slice(0, 5));
    } catch (error) {
      console.error("Error analyzing progress:", error);
    } finally {
      setLoading(false);
    }
  }, [userID, projectID, projectProgress, categoryScores]);

  useEffect(() => {
    analyzeProgress();
  }, [analyzeProgress]);

  if (loading) {
    return (
      <div className={`card ${className}`}>
        <div className="card-body py-8 text-center">
          <div className="spinner-border spinner-border-sm text-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card ${className}`}>
      <div className="card-header border-0 pt-5 pb-0">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bolder text-dark fs-3">
            <i className="bi bi-list-check text-primary me-2"></i>
            Action Items
          </span>
          <span className="text-muted mt-1 fw-bold fs-7">
            {categoryScores
              ? "Prioritised by category weakness score"
              : "Prioritised tasks to improve your score"}
          </span>
        </h3>
      </div>

      <div className="card-body pt-4">
        {recommendations.length === 0 ? (
          <div className="text-center py-6">
            <i className="bi bi-check-circle text-success fs-2x d-block mb-3"></i>
            <span className="text-gray-600 fs-7">All caught up! No urgent actions.</span>
          </div>
        ) : (
          <div className="d-flex flex-column gap-0">
            {recommendations.map((rec, index) => {
              const catColor = CATEGORY_COLORS[rec.category] || {
                solid: "#3B82F6",
                light: "#DBEAFE",
              };
              const priorityLabel =
                rec.priority === "critical"
                  ? "Critical"
                  : rec.priority === "important"
                  ? "Up Next"
                  : "Suggested";
              const priorityTextColor =
                rec.priority === "critical"
                  ? "#EF4444"
                  : rec.priority === "important"
                  ? "#3B82F6"
                  : "#64748B";
              const priorityBg =
                rec.priority === "critical"
                  ? "#FEE2E2"
                  : rec.priority === "important"
                  ? "#DBEAFE"
                  : "#F1F5F9";

              return (
                <Link
                  key={rec.stepNumber}
                  to={`/step${rec.stepNumber}`}
                  state={{ projectTitle, projectID, userID }}
                  className="text-decoration-none"
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      padding: "12px 8px",
                      borderRadius: 8,
                      transition: "background 0.15s ease",
                      borderBottom:
                        index < recommendations.length - 1
                          ? "1px solid #F1F5F9"
                          : "none",
                      cursor: "pointer",
                    }}
                    className="bg-hover-light"
                  >
                    {/* Category-coloured icon circle */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        borderRadius: "50%",
                        background: catColor.light,
                        marginRight: 12,
                        marginTop: 2,
                      }}
                    >
                      <i
                        className={`bi ${rec.icon}`}
                        style={{ fontSize: 14, color: catColor.solid }}
                      ></i>
                    </div>

                    {/* Content */}
                    <div style={{ flexGrow: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 6,
                          marginBottom: 2,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#0F172A",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          {rec.title}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            padding: "1px 7px",
                            borderRadius: 20,
                            background: priorityBg,
                            color: priorityTextColor,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {priorityLabel}
                        </span>
                      </div>

                      {/* Category + score (when AI scores available) */}
                      {rec.categoryScore !== undefined && (
                        <div
                          style={{
                            fontSize: 11,
                            color: catColor.solid,
                            fontWeight: 500,
                            marginBottom: 2,
                          }}
                        >
                          {CATEGORY_LABELS[rec.category] || rec.category} &mdash;{" "}
                          {Math.round(rec.categoryScore)}% score
                        </div>
                      )}

                      <span style={{ fontSize: 12, color: "#64748B" }}>
                        {rec.reason}
                      </span>
                    </div>

                    {/* Step number + arrow */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginLeft: 8,
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          padding: "2px 8px",
                          borderRadius: 20,
                          background: "#F1F5F9",
                          color: "#475569",
                          marginRight: 6,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Step {rec.stepNumber}
                      </span>
                      <i
                        className="bi bi-chevron-right"
                        style={{ fontSize: 12, color: "#94A3B8" }}
                      ></i>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
